import { type ReactNode, useEffect } from "react";

import { router, usePage } from "@inertiajs/react";

import { CookieConsentBanner } from "@/components/cookie-consent-banner";
import { InertiaObservers } from "@/components/inertia-observers";
import { Toaster } from "@/components/ui/sonner";
import { useFlash } from "@/hooks/use-flash";
import type { SharedProps } from "@/serializers";

declare global {
  interface Window {
    Beacon: (method: string, ...args: unknown[]) => void;
  }
}

interface PersistentLayoutProps {
  children: ReactNode;
}

const HELPSCOUT_BEACON_ID = "d2d12b9e-304f-404a-8a19-172044de8df2";
const HELPSCOUT_BEACON_SCRIPT_ID = "helpscout-beacon-loader";

function ensureHelpScoutBeaconLoaded() {
  if (typeof window === "undefined") return;
  if (typeof window.Beacon === "function") {
    window.Beacon("init", HELPSCOUT_BEACON_ID);
    return;
  }

  window.Beacon = function beacon(method: string, ...args: unknown[]) {
    const queuedBeacon = beacon as typeof beacon & {
      readyQueue?: Array<{ method: string; args: unknown[] }>;
    };

    queuedBeacon.readyQueue ??= [];
    queuedBeacon.readyQueue.push({ method, args });
  };

  const existingScript = document.getElementById(HELPSCOUT_BEACON_SCRIPT_ID);
  if (existingScript) return;

  const script = document.createElement("script");
  script.id = HELPSCOUT_BEACON_SCRIPT_ID;
  script.async = true;
  script.src = "https://beacon-v2.helpscout.net";
  script.addEventListener("load", () => {
    if (typeof window.Beacon === "function") {
      window.Beacon("init", HELPSCOUT_BEACON_ID);
    }
  });

  document.head.appendChild(script);
}

export default function PersistentLayout({ children }: PersistentLayoutProps) {
  useFlash();

  const { auth } = usePage<SharedProps>().props;

  const userId = auth?.user?.id;
  const userName = auth?.user?.name;
  const userEmail = auth?.user?.email;
  const userOrganizationName = auth?.user?.organizationName;
  const beaconSignature = auth?.beaconSignature;

  useEffect(() => {
    ensureHelpScoutBeaconLoaded();
  }, []);

  useEffect(() => {
    if (!userId || typeof window.Beacon === "undefined") return;

    window.Beacon("identify", {
      name: userName,
      email: userEmail,
      company: userOrganizationName ?? undefined,
      signature: beaconSignature,
      "User-Id": String(userId),
    });

    return () => {
      window.Beacon("logout");
    };
  }, [beaconSignature, userEmail, userId, userName, userOrganizationName]);

  useEffect(() => {
    const removeListener = router.on("navigate", () => {
      if (typeof window.Beacon === "undefined") return;
      window.Beacon("event", {
        type: "page-viewed",
        url: document.location.href,
        title: document.title,
      });
    });
    return removeListener;
  }, []);

  return (
    <>
      <InertiaObservers />
      {children}
      <CookieConsentBanner />
      <Toaster richColors />
    </>
  );
}
